/**
 * Client knowledge-base field registry — single source of truth for the
 * editable knowledge schema (Phase 3a Step 4).
 *
 * Knowledge is stored as ClientKnowledge key/value rows keyed by
 * (clientId, category, key). This registry maps each logical field to its
 * (category, key) pair, a human label, an input type, and whether it's
 * visible on the client-facing "Our Services" tab.
 *
 * Imported by:
 *   - @prism/api  — to resolve a field key → category on update, and to gate
 *                   the client-facing endpoint to client-visible fields
 *   - @prism/web  — to render the admin Knowledge Base tab + "Our Services"
 *   - @prism/mcp  — prism_update_client_knowledge resolves section → field
 *
 * NOTE: keys are globally unique across sections. Some fields reuse the
 * import's original category (e.g. brandNotes lives under "profile"); new
 * editable fields use new categories ("brand", "goals"). The full record —
 * including import-only categories not surfaced here (contacts, website,
 * email, ads, ops) — is still returned by GET /api/clients/:id/knowledge.
 */

export const KNOWLEDGE_SECTIONS = [
  {
    key: "profile",
    label: "Business Profile",
    fields: [
      { key: "businessDescription", category: "profile", label: "Business Description", type: "textarea" },
      { key: "size",                category: "profile", label: "Size",                  type: "text" },
      { key: "annualRevenue",       category: "profile", label: "Annual Revenue",        type: "text" },
      { key: "vendors",             category: "profile", label: "Vendors / Brands",      type: "textarea" },
      { key: "memberships",         category: "profile", label: "Memberships",           type: "textarea" },
      { key: "idealClientProfile",  category: "profile", label: "Ideal Client Profile",  type: "textarea" },
    ],
  },
  {
    key: "contract",
    label: "Contracted Services",
    fields: [
      { key: "serviceTypes",        category: "profile",  label: "Service Types",          type: "textarea", clientVisible: true },
      { key: "contractedServices",  category: "contract", label: "Contracted Services",    type: "textarea", clientVisible: true },
      { key: "accountType",         category: "contract", label: "Account Type",           type: "text" },
      { key: "contractRenewalDate", category: "contract", label: "Contract Renewal Date",  type: "text",     clientVisible: true },
      // Agency Cockpit v1 contract stopgap (spec §7): reserved keys the
      // cockpit's Services & Contract panel reads until Phase 4b's structured
      // Contract model lands. snake_case is intentional — the literal
      // ClientKnowledge.key values are the wire contract with the cockpit.
      { key: "services",       category: "contract", label: "Services (one per line)",     type: "textarea" },
      { key: "contract_start", category: "contract", label: "Contract Start (YYYY-MM-DD)", type: "text" },
      { key: "contract_end",   category: "contract", label: "Contract End (YYYY-MM-DD)",   type: "text" },
      { key: "contract_value", category: "contract", label: "Contract Value",              type: "text" },
      { key: "terms",          category: "contract", label: "Contract Terms",              type: "textarea" },
    ],
  },
  {
    key: "brand",
    label: "Brand Voice",
    fields: [
      { key: "brandVoice",        category: "brand",   label: "Brand Voice",        type: "textarea" },
      { key: "brandNotes",        category: "profile", label: "Brand Notes",        type: "textarea" },
      { key: "contentGuidelines", category: "brand",   label: "Content Guidelines", type: "textarea" },
    ],
  },
  {
    key: "social",
    label: "Social & Tools",
    fields: [
      { key: "socialAccounts", category: "social", label: "Social Accounts", type: "json" },
      { key: "connectedTools", category: "social", label: "Connected Tools", type: "json" },
      { key: "postAllocation", category: "social", label: "Post Allocation", type: "text" },
      { key: "postTypes",      category: "social", label: "Post Types",      type: "text" },
    ],
  },
  {
    key: "goals",
    label: "Goals",
    fields: [
      { key: "goals", category: "goals", label: "Goals", type: "textarea", clientVisible: true },
    ],
  },
];

/** Flat list of every registered field. */
export const KNOWLEDGE_FIELDS = KNOWLEDGE_SECTIONS.flatMap((s) => s.fields);

/** key → field descriptor (for resolving category on update). */
export const KNOWLEDGE_FIELD_BY_KEY = Object.fromEntries(
  KNOWLEDGE_FIELDS.map((f) => [f.key, f])
);

/** Fields shown read-only on the client-facing "Our Services" tab. */
export const CLIENT_VISIBLE_FIELDS = KNOWLEDGE_FIELDS.filter((f) => f.clientVisible);

/** Note category tags surfaced in the admin Knowledge Base notes UI. */
export const NOTE_CATEGORIES = ["general", "campaign", "client-feedback", "strategy"];
